import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import type { CustomerContact, CreateContactDto, UpdateContactDto } from '../../types/customer360';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateContactDto | UpdateContactDto) => Promise<void>;
  contact?: CustomerContact;
  customerId: number;
}

const CONTACT_TYPES = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'work_phone', label: 'Work Phone' },
  { value: 'fax', label: 'Fax' },
  { value: 'other', label: 'Other' }
] as const;

export default function ContactModal({ isOpen, onClose, onSave, contact, customerId }: ContactModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [formData, setFormData] = useState({
    contact_type: contact?.contact_type || 'email' as const,
    contact_value: contact?.contact_value || '',
    is_primary: contact?.is_primary || false,
    label: contact?.label || '',
    verified: contact?.verified || false
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact) {
      setFormData({
        contact_type: contact.contact_type,
        contact_value: contact.contact_value,
        is_primary: contact.is_primary,
        label: contact.label || '',
        verified: contact.verified
      });
    } else {
      setFormData({
        contact_type: 'email',
        contact_value: '',
        is_primary: false,
        label: '',
        verified: false
      });
    }
    setErrors({});
  }, [contact, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contact_value.trim()) {
      newErrors.contact_value = 'Contact value is required';
    } else if (formData.contact_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_value)) {
      newErrors.contact_value = 'Invalid email format';
    } else if (['phone', 'mobile', 'work_phone', 'fax'].includes(formData.contact_type) && !/^[\d\s\-+()]+$/.test(formData.contact_value)) {
      newErrors.contact_value = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (contact) {
        // Update existing contact
        await onSave(formData);
      } else {
        // Create new contact
        await onSave({
          ...formData,
          customer_id: customerId
        });
      }
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`relative w-full max-w-md rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between border-b p-4 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {contact ? 'Edit Contact' : 'Add New Contact'}
            </h3>
            <button
              onClick={onClose}
              className={`rounded-lg p-1 transition-colors ${
                isDark 
                  ? 'hover:bg-gray-700 text-gray-400' 
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Contact Type */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Contact Type *
              </label>
              <select
                value={formData.contact_type}
                onChange={(e) => setFormData({ ...formData, contact_type: e.target.value as any })}
                className={`w-full px-3 py-2 rounded-md border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {CONTACT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contact Value */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {formData.contact_type === 'email' ? 'Email Address' : 'Phone Number'} *
              </label>
              <input
                type="text"
                value={formData.contact_value}
                onChange={(e) => setFormData({ ...formData, contact_value: e.target.value })}
                className={`w-full px-3 py-2 rounded-md border ${
                  errors.contact_value 
                    ? 'border-red-500' 
                    : isDark 
                      ? 'border-gray-600' 
                      : 'border-gray-300'
                } ${
                  isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder={formData.contact_type === 'email' ? 'email@example.com' : '+1 234 567 8900'}
              />
              {errors.contact_value && (
                <p className="mt-1 text-sm text-red-500">{errors.contact_value}</p>
              )}
            </div>

            {/* Label */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Label (Optional)
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className={`w-full px-3 py-2 rounded-md border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="e.g., Personal, Work"
              />
            </div>

            {/* Checkboxes */}
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_primary}
                  onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className={`ml-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Set as primary contact
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.verified}
                  onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className={`ml-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Verified contact
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-md border transition-colors ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : (contact ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
